import { useMapEvents } from "react-leaflet";
import L from 'leaflet';
import { useRef, useEffect } from 'react';

const MapEvents = ({ setPosition, mapMode = 'map', groupedTargets = {}, onMerge, onSetStatus }) => {
  const startPoint = useRef(null);
  const rect = useRef(null);

  const map = useMapEvents({
    mousemove(e) {
      setPosition(e.latlng);
      const isRectMode = mapMode === 'merge' || (typeof mapMode === 'string' && mapMode.startsWith('status_'));
      if (isRectMode && startPoint.current && rect.current) {
        const bounds = L.latLngBounds(startPoint.current, e.latlng);
        rect.current.setBounds(bounds);
      }
    },
    mousedown(e) {
      const isRectMode = mapMode === 'merge' || (typeof mapMode === 'string' && mapMode.startsWith('status_'));
      if (isRectMode) {
        // clear any previous rectangle before starting a new one
        if (rect.current) {
          try { rect.current.remove(); } catch (err) {}
          rect.current = null;
        }
        startPoint.current = e.latlng;
        rect.current = L.rectangle([startPoint.current, startPoint.current], { color: '#00ffff', weight: 1, fillOpacity: 0.15 }).addTo(map);
      }
    },
    mouseup(e) {
      const isRectMode = mapMode === 'merge' || (typeof mapMode === 'string' && mapMode.startsWith('status_'));
      if (isRectMode && startPoint.current && rect.current) {
        const bounds = rect.current.getBounds();
        // if the drawn rectangle is extremely small on screen (user click/drag noise), ignore it
        try {
          const ne = bounds.getNorthEast();
          const sw = bounds.getSouthWest();
          const pNE = map.latLngToContainerPoint(ne);
          const pSW = map.latLngToContainerPoint(sw);
          const pixelDiag = pNE.distanceTo(pSW);
          const MIN_PIXEL_DIAG = 8; // pixels - threshold for ignoring tiny rectangles
          if (pixelDiag < MIN_PIXEL_DIAG) {
            // cleanup and skip merge
            if (rect.current) {
              rect.current.remove();
              rect.current = null;
            }
            startPoint.current = null;
            return;
          }
        } catch (err) {
          // if any error occurs computing pixel size, continue with selection
        }
        // find targets within bounds
        const selectedIds = [];
        Object.entries(groupedTargets).forEach(([key, targets]) => {
          const lastTarget = targets[targets.length - 1];
          // merged targets are hidden on the map: only their base can be picked
          if (lastTarget?.merged === true) return;
          const splinePoints = lastTarget?.spline_points || [];
          if (!Array.isArray(splinePoints) || splinePoints.length === 0) return;
          const valid = splinePoints.filter(pt => pt && pt.latitude != null && pt.longitude != null);
          if (valid.length === 0) return;
          const lastPoint = valid[valid.length - 1];
          const latlng = L.latLng(lastPoint.latitude, lastPoint.longitude);
          if (bounds.contains(latlng)) selectedIds.push(lastTarget._id);
        });
        // cleanup
        if (rect.current) {
          rect.current.remove();
          rect.current = null;
        }
        startPoint.current = null;
        if (selectedIds.length > 0) {
          // status modes: mapMode like 'status_enemy'
          if (typeof mapMode === 'string' && mapMode.startsWith('status_') && typeof onSetStatus === 'function') {
            const status = mapMode.split('_')[1];
            onSetStatus(selectedIds, status);
          } else if (typeof onMerge === 'function') {
            onMerge(selectedIds);
          }
        }
      } else {
        // if not in merge mode, clear any existing rectangle safely
        if (rect.current) {
          rect.current.remove();
          rect.current = null;
        }
        startPoint.current = null;
      }
    }
  });

  useEffect(() => {
    if (!map) return;
    try {
      const isRectMode = mapMode === 'merge' || (typeof mapMode === 'string' && mapMode.startsWith('status_'));
      if (isRectMode) {
        if (map.dragging && map.dragging.enabled()) map.dragging.disable();
        if (map.boxZoom && map.boxZoom.enabled()) map.boxZoom.disable();
      } else {
        if (map.dragging && !map.dragging.enabled()) map.dragging.enable();
        if (map.boxZoom && !map.boxZoom.enabled()) map.boxZoom.enable();
      }
    } catch (err) {
      // ignore
    }
    return () => {
      try {
        if (map.dragging && !map.dragging.enabled()) map.dragging.enable();
        if (map.boxZoom && !map.boxZoom.enabled()) map.boxZoom.enable();
      } catch (err) {}
    };
  }, [mapMode, map]);

  return null;
};

export default MapEvents;
